/**
 * Browser capability checks run before anything else, with a clear message (in the import screen's
 * style — no game art exists yet) when the browser can't run the game: WebGL 2 for drawing, the
 * Origin Private File System for keeping the player's archives, a way to write to it, and enough
 * storage quota for the archives.
 */
import './import.css';
import { canWriteWithWorker, hasCreateWritable } from './storage';

export const WORKING_BROWSERS = [
  'Google Chrome or Microsoft Edge 102 or newer',
  'Mozilla Firefox 111 or newer',
  'Safari 16.4 or newer on macOS',
];

export type Problem = 'webgl2' | 'opfs' | 'opfs-write';

/**
 * Phones and tablets: the game needs a keyboard, a mouse and a few GB of storage, so they get
 * the title page and a note when they press Play. iPadOS reports itself as a Mac with touch.
 */
export function isMobile(): boolean {
  const ua = navigator.userAgent;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export function hasWebGL2(): boolean {
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2');
    (gl?.getExtension('WEBGL_lose_context') as { loseContext(): void } | null)?.loseContext();
    return !!gl;
  } catch {
    return false;
  }
}

/** Whether OPFS works here (private windows often refuse it) and can be written to. */
export async function storageProblem(): Promise<Problem | null> {
  try {
    if (!navigator.storage?.getDirectory) return 'opfs';
    await navigator.storage.getDirectory();
  } catch {
    return 'opfs';
  }
  if (hasCreateWritable()) return null;
  try {
    return (await canWriteWithWorker()) ? null : 'opfs-write';
  } catch {
    return 'opfs-write';
  }
}

/** The first thing this browser lacks, or null when it can run the game. */
export async function checkBrowser(): Promise<Problem | null> {
  if (!hasWebGL2()) return 'webgl2';
  return storageProblem();
}

const TEXT: Record<Problem, { title: string; body: string }> = {
  webgl2: {
    title: 'WebGL 2 is not available',
    body: 'd2o draws the game with WebGL 2, which this browser does not offer right now. It may be switched off: turn on hardware (graphics) acceleration in the browser settings and restart the browser, or update your graphics drivers.',
  },
  opfs: {
    title: 'Browser storage is not available',
    body: 'd2o keeps the archives from your own discs in this browser’s private storage (the Origin Private File System) so that they never leave your computer and only need importing once. This browser does not offer that storage here. Private and incognito windows usually block it — open d2o in a normal window.',
  },
  'opfs-write': {
    title: 'Browser storage is read-only',
    body: 'This browser has private storage but cannot write files into it, so your disc archives could not be kept. Update the browser to a newer version.',
  },
};

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/** Replaces the page with an explanation; the game does not start. */
export function showUnsupported(root: HTMLElement, problem: Problem): void {
  const t = TEXT[problem];
  root.innerHTML = `
    <div class="import">
      <div class="card notice" role="alert">
        <h1>d2o</h1>
        <div class="sub">${esc(t.title)}</div>
        <p>${esc(t.body)}</p>
        <p>d2o works in:</p>
        <ul class="browsers">${WORKING_BROWSERS.map((b) => `<li>${esc(b)}</li>`).join('')}</ul>
        <div class="legal"><a href="about.html">About</a></div>
      </div>
    </div>`;
}

/** Text for the import screen when the archives will not fit. */
export function quotaMessage(needed: number, free: number): string {
  const mb = (n: number) => `${Math.ceil(n / (1 << 20))} MB`;
  return `Not enough browser storage: the archives need about ${mb(needed)}, and this browser allows ${mb(Math.max(0, free))} more for this site. Free up disk space, leave private browsing, or use ${WORKING_BROWSERS.slice(0, 2).join(' or ')}.`;
}
